import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule } from '@angular/forms';

import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSortModule } from '@angular/material/sort';

import { ChcpRoutes } from '@cigna/chcp/shared/util';

import { ChcpSharedUiModule } from '@cigna/chcp/shared/ui';

import { UiTabsModule } from '@cigna/ui/tabs';
import { UiInputModule } from '@cigna/ui/input';
import { UiButtonModule } from '@cigna/ui/button';
import { UiIconModule } from '@cigna/ui/icon';
import { UiCollapsibleModule } from '@cigna/ui/collapsible';
import { AnalyticsUiModule } from '@cigna/analytics/ui';

import { TranslateModule } from '@ngx-translate/core';

import { DialogAddusersRemoveComponent } from './dialog-addusers-remove/dialog-addusers-remove.component';
import { DialogAssignmentRemoveUserComponent } from './dialog-assignment-remove-user/dialog-assignment-remove-user.component';
import { DialogCancelComponent } from './dialog-cancel/dialog-cancel.component';
import { DialogUserRemoveAccessComponent } from './dialog-user-remove-access/dialog-user-remove-access.component';
import { DialogUserResetPasswordComponent } from './dialog-user-reset-password/dialog-user-reset-password.component';
import { DialogUserViewAccessComponent } from './dialog-user-view-access/dialog-user-view-access.component';
import { DialogWamHelpFaqComponent } from './dialog-wam-help-faq/dialog-wam-help-faq.component';
import { PageStepNavigationComponent } from './page-step-navigation/page-step-navigation.component';
import { EntitlementGroupComponent } from './entitlement-group/entitlement-group.component';
import { UserOverviewTableComponent } from './user-overview-table/user-overview-table.component';
import { UserStatusItemComponent } from './user-status-item/user-status-item.component';
import { UserSummaryItemComponent } from './user-summary-item/user-summary-item.component';
import { UserTinsGroupedItemComponent } from './user-tins-grouped-item/user-tins-grouped-item.component';
import { TableReviewConfirmComponent } from './table-review-confirm/table-review-confirm.component';
import { UserAccessDiffComponent } from './user-access-diff/user-access-diff.component';

import { PageOverviewComponent } from './page-overview/page-overview.component';
import { PageAddNewUsersComponent } from './page-add-new-users/page-add-new-users.component';
import { PageChooseAssignmentMethodComponent } from './page-choose-assignment-method/page-choose-assignment-method.component';

import { ManageUserAccessEffects } from './+state/manage-user-access.effects';
import {
  MANAGE_ACCESS_FEATURE_KEY,
  manageUserAccessReducer,
} from './+state/manage-user-access.reducer';
import { PageAssignAccessIndividualComponent } from './page-assign-access-individual/page-assign-access-individual.component';
import { PageAssignAccessBulkComponent } from './page-assign-access-bulk/page-assign-access-bulk.component';
import { PageReviewChangesComponent } from './page-review-changes/page-review-changes.component';
import { PageSubmitSuccessComponent } from './page-submit-success/page-submit-success.component';
import { LetDirective } from '@ngrx/component';
import { UserLobComponent } from './user-lob/user-lob.component';
import { DialogUpdateLobPrefComponent } from './dialog-update-lob-pref/dialog-update-lob-pref.component';
import { PageReviewLobPrefComponent } from './page-review-lob-pref/page-review-lob-pref.component';
import { DatatableActionLinksComponent } from './datatable-action-links/datatable-action-links.component';

import { ChcpSharedEnvironmentUtilModule } from '@cigna/chcp/shared/environment-util';
import { WamsHelpDropdownComponent } from './wams-help-dropdown/wams-help-dropdown.component';
import { UiPopoverModule } from '@cigna/ui/popover';

const routes: ChcpRoutes = [
  {
    path: '',
    pathMatch: 'full',
    component: PageOverviewComponent,
    data: {
      title: 'Manage User Access',
      analytics: {
        name: 'Manage User Access',
        // TODO: resolve CHCP disables by bot
        // eslint-disable-next-line sonarjs/no-duplicate-string
        section: 'Working With Cigna',
        prop13: 'pv-WC004-Working Cigna',
      },
    },
  },
  {
    path: 'new',
    component: PageAddNewUsersComponent,
    data: {
      title: 'Manage User Access - Identify Users',
      analytics: {
        name: 'Manage User Access Identify Users',
        section: 'Working With Cigna',
        // TODO: resolve CHCP disables by bot
        // eslint-disable-next-line sonarjs/no-duplicate-string
        prop13: 'pv-WC011-Add User',
      },
    },
  },
  {
    path: 'assignment-method',
    component: PageChooseAssignmentMethodComponent,
    data: {
      title: 'Manage User Access - How Do You Want to Assign Access',
      analytics: {
        name: 'Manage User Access How Do You Want to Assign Access',
        section: 'Working With Cigna',
        prop13: 'pv-WC011-Add User',
      },
    },
  },
  {
    path: 'access-individual',
    component: PageAssignAccessIndividualComponent,
    data: {
      title: 'Manage User Access - Assign TIN & Levels Of Access',
      analytics: {
        name: 'Manage User Access Assign TIN & Levels Of Access',
        section: 'Working With Cigna',
        prop13: 'pv-WC011-Add User',
      },
    },
  },
  {
    path: 'access-bulk',
    component: PageAssignAccessBulkComponent,
    data: {
      title: 'Manage User Access - Assign TIN & Levels Of Access',
      analytics: {
        name: 'Manage User Access Assign TIN & Levels Of Access',
        section: 'Working With Cigna',
        prop13: 'pv-WC011-Add User',
      },
    },
  },
  {
    path: 'review',
    component: PageReviewChangesComponent,
    data: {
      title: 'Manage User Access - Confirm',
      analytics: {
        name: 'Manage User Access Confirm',
        section: 'Working With Cigna',
        prop13: 'pv-WC011-Add User',
      },
    },
  },
  {
    path: 'lob-pref',
    component: PageReviewLobPrefComponent,
    data: {
      title: 'Manage User Access - Confirm Line of Business',
      analytics: {
        name: 'Manage User Access Confirm Line of Business',
        section: 'Working With Cigna',
        prop13: 'pv-WC011-Add User',
      },
    },
  },
  {
    path: 'success',
    component: PageSubmitSuccessComponent,
    data: {
      title: 'Manage User Access - Receive Credentials',
      analytics: {
        name: 'Manage User Access Receive Credentials',
        section: 'Working With Cigna',
        prop13: 'pv-WC011-Add User',
      },
    },
  },
];

@NgModule({
  imports: [
    CommonModule,
    ChcpSharedUiModule,
    UiInputModule,
    UiButtonModule,
    UiIconModule,
    UiTabsModule,
    UiPopoverModule,
    UiCollapsibleModule,
    ReactiveFormsModule.withConfig({
      callSetDisabledState: 'whenDisabledForLegacyCode',
    }),
    LetDirective,
    ChcpSharedEnvironmentUtilModule,
    MatDialogModule,
    MatSortModule,
    AnalyticsUiModule,
    TranslateModule,
    StoreModule.forFeature(MANAGE_ACCESS_FEATURE_KEY, manageUserAccessReducer),
    EffectsModule.forFeature([ManageUserAccessEffects]),
    RouterModule.forChild(routes),
  ],
  declarations: [
    PageOverviewComponent,
    DialogAddusersRemoveComponent,
    DialogAssignmentRemoveUserComponent,
    DialogCancelComponent,
    DialogUserRemoveAccessComponent,
    DialogUserResetPasswordComponent,
    DialogUserViewAccessComponent,
    PageStepNavigationComponent,
    EntitlementGroupComponent,
    UserOverviewTableComponent,
    UserStatusItemComponent,
    UserSummaryItemComponent,
    UserTinsGroupedItemComponent,
    TableReviewConfirmComponent,
    UserAccessDiffComponent,
    PageAddNewUsersComponent,
    PageChooseAssignmentMethodComponent,
    PageAssignAccessIndividualComponent,
    PageAssignAccessBulkComponent,
    PageReviewChangesComponent,
    PageSubmitSuccessComponent,
    UserLobComponent,
    DialogUpdateLobPrefComponent,
    PageReviewLobPrefComponent,
    DatatableActionLinksComponent,
    DialogWamHelpFaqComponent,
    WamsHelpDropdownComponent,
  ],
})
export class ChcpWorkingWithCignaManageAccessFeatureModule {}
